import { useState, useRef, useEffect, useCallback, memo } from 'react';
import { LuX, LuSettings } from 'react-icons/lu';
import CallToActionCard from './CallToActionCard';
import JobAlertsCard from './JobAlertsCard.jsx';
import OpenJobsCard from './OpenJobsCard';
import LeaderboardCard from './LeaderboardCard';
import YourReferralsCard from './YourReferralsCard';
import PointsRankingCard from './PointsRankingCard';
import ReferralsRankingCard from './ReferralsRankingCard';
import CustomHtmlCard from './CustomHtmlCard';
import AnnouncementsCard from './AnnouncementsCard';
import RecentJobsCard from './RecentJobsCard';
import AnnouncementThumbnailCard from './AnnouncementThumbnailCard';
import RecognitionShoutoutsCard from './RecognitionShoutoutsCard';
import MyRecognitionsCard from './MyRecognitionsCard';
import RecognitionLeaderboardCard from './RecognitionLeaderboardCard';
import CardSettingsModal from './CardSettingsModal';
import { CARD_TYPES } from '../cardTypes';

const Card = memo(function Card({
	card,
	onDelete,
	onResize,
	onResizeStart,
	onResizeEnd,
	onDragMove,
	onDragEnd,
	onUpdateCardData,
	dragOffset,
	columnWidth,
	rowHeight,
	horizontalMargin,
	verticalMargin,
	maxColumns,
	isLocked = false,
	isEditMode = true,
	userStats,
	announcements,
	leaderboards,
	userStatsLoading,
	isAdminView,
	currentUser,
	setCurrentUser,
	departments,
	subCompanies,
	jobNotificationSetting,
	hideSubCompany,
	hotSearchedJobs,
	recognitionShoutouts,
	myRewards,
}) {
	const [isHovered, setIsHovered] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const [isResizing, setIsResizing] = useState(false);
	const [resizeMode, setResizeMode] = useState('both');
	const [resizePreview, setResizePreview] = useState(null);
	const [hoveredHandle, setHoveredHandle] = useState(null);
	const [showSettings, setShowSettings] = useState(false);
	const cardRef = useRef(null);
	const startPos = useRef({
		x: 0,
		y: 0,
		cardX: 0,
		cardY: 0,
		cardWidth: 0,
		cardHeight: 0,
	});

	const handleMouseDown = useCallback(
		(e) => {
			if (isLocked || !isEditMode) return;
			const target = e.target;

			// Don't drag if clicking on interactive elements
			if (
				target.closest('.resize-handle') ||
				target.closest('.delete-button') ||
				target.closest('.settings-button') ||
				target.closest('input') ||
				target.closest('textarea') ||
				target.closest('button') ||
				target.closest('a') ||
				target.tagName === 'INPUT' ||
				target.tagName === 'TEXTAREA' ||
				target.tagName === 'BUTTON' ||
				target.tagName === 'A' ||
				target.hasAttribute('contenteditable') ||
				target.closest('[data-no-drag]')
			) {
				return;
			}

			setIsDragging(true);
			startPos.current = {
				x: e.clientX,
				y: e.clientY,
				cardX: card.x,
				cardY: card.y,
				cardWidth: card.width,
				cardHeight: card.height,
			};
		},
		[isLocked, isEditMode, card]
	);

	const handleResizeMouseDown = useCallback(
		(e, mode) => {
			if (isLocked || !isEditMode) return;
			e.stopPropagation();

			setIsResizing(true);
			setResizeMode(mode);
			onResizeStart();
			startPos.current = {
				x: e.clientX,
				y: e.clientY,
				cardX: card.x,
				cardY: card.y,
				cardWidth: card.width,
				cardHeight: card.height,
			};
		},
		[isLocked, isEditMode, card, onResizeStart]
	);

	useEffect(() => {
		const handleMouseMove = (e) => {
			if (isDragging) {
				const deltaX = e.clientX - startPos.current.x;
				const deltaY = e.clientY - startPos.current.y;

				onDragMove(deltaX, deltaY);
			} else if (isResizing) {
				const deltaX = e.clientX - startPos.current.x;
				const deltaY = e.clientY - startPos.current.y;

				const startPixelWidth =
					startPos.current.cardWidth * columnWidth +
					(startPos.current.cardWidth - 1) * horizontalMargin;
				const startPixelHeight =
					startPos.current.cardHeight * rowHeight +
					(startPos.current.cardHeight - 1) * verticalMargin;

				let newPixelWidth = startPixelWidth;
				let newPixelHeight = startPixelHeight;

				if (resizeMode === 'both' || resizeMode === 'width') {
					newPixelWidth = Math.max(columnWidth, startPixelWidth + deltaX);
				}
				if (resizeMode === 'both' || resizeMode === 'height') {
					newPixelHeight = Math.max(rowHeight, startPixelHeight + deltaY);
				}

				setResizePreview({ width: newPixelWidth, height: newPixelHeight });

				const gridDeltaX = Math.round(
					deltaX / (columnWidth + horizontalMargin)
				);
				const gridDeltaY = Math.round(deltaY / (rowHeight + verticalMargin));

				let newWidth = startPos.current.cardWidth;
				let newHeight = startPos.current.cardHeight;

				if (resizeMode === 'both' || resizeMode === 'width') {
					newWidth = Math.max(
						1,
						Math.min(
							maxColumns - card.x,
							startPos.current.cardWidth + gridDeltaX
						)
					);
				}
				if (resizeMode === 'both' || resizeMode === 'height') {
					newHeight = Math.max(1, startPos.current.cardHeight + gridDeltaY);
				}

				if (newWidth !== card.width || newHeight !== card.height) {
					onResize(newWidth, newHeight);
				}
			}
		};

		const handleMouseUp = (e) => {
			if (isDragging) {
				const deltaX = e.clientX - startPos.current.x;
				const deltaY = e.clientY - startPos.current.y;

				const gridDeltaX = Math.round(
					deltaX / (columnWidth + horizontalMargin)
				);
				const gridDeltaY = Math.round(deltaY / (rowHeight + verticalMargin));

				const newX = Math.max(
					0,
					Math.min(maxColumns - card.width, startPos.current.cardX + gridDeltaX)
				);
				const newY = Math.max(0, startPos.current.cardY + gridDeltaY);

				onDragEnd(newX, newY);
			} else if (isResizing) {
				const deltaX = e.clientX - startPos.current.x;
				const deltaY = e.clientY - startPos.current.y;

				const gridDeltaX = Math.round(
					deltaX / (columnWidth + horizontalMargin)
				);
				const gridDeltaY = Math.round(deltaY / (rowHeight + verticalMargin));

				let newWidth = startPos.current.cardWidth;
				let newHeight = startPos.current.cardHeight;

				if (resizeMode === 'both' || resizeMode === 'width') {
					newWidth = Math.max(
						1,
						Math.min(
							maxColumns - card.x,
							startPos.current.cardWidth + gridDeltaX
						)
					);
				}
				if (resizeMode === 'both' || resizeMode === 'height') {
					newHeight = Math.max(1, startPos.current.cardHeight + gridDeltaY);
				}

				const sizeChanged =
					newWidth !== card.width || newHeight !== card.height;
				if (sizeChanged) onResize(newWidth, newHeight);
				setResizePreview(null);
				onResizeEnd();
			}
			setIsDragging(false);
			setIsResizing(false);
		};

		if (isDragging || isResizing) {
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
			return () => {
				document.removeEventListener('mousemove', handleMouseMove);
				document.removeEventListener('mouseup', handleMouseUp);
			};
		}
	}, [
		isDragging,
		isResizing,
		resizeMode,
		card,
		columnWidth,
		rowHeight,
		horizontalMargin,
		verticalMargin,
		maxColumns,
		onDragMove,
		onResize,
		onDragEnd,
		onResizeEnd,
	]);

	const pixelWidth =
		card.width * columnWidth + (card.width - 1) * horizontalMargin;
	const pixelHeight =
		card.height * rowHeight + (card.height - 1) * verticalMargin;
	const pixelX = card.x * (columnWidth + horizontalMargin);
	const pixelY = card.y * (rowHeight + verticalMargin);

	const displayX =
		isDragging && dragOffset
			? startPos.current.cardX * (columnWidth + horizontalMargin) + dragOffset.x
			: pixelX;
	const displayY =
		isDragging && dragOffset
			? startPos.current.cardY * (rowHeight + verticalMargin) + dragOffset.y
			: pixelY;
	const displayWidth = resizePreview ? resizePreview.width : pixelWidth;
	const displayHeight = resizePreview ? resizePreview.height : pixelHeight;

	const getCursorClass = () => {
		if (isDragging) return 'cursor-grabbing';
		if (isResizing) {
			if (resizeMode === 'width') return 'cursor-ew-resize';
			if (resizeMode === 'height') return 'cursor-ns-resize';
			return 'cursor-nwse-resize';
		}
		return 'cursor-grab';
	};

	const handleUpdateCardData = useCallback(
		(newData) => {
			if (onUpdateCardData) {
				onUpdateCardData(card.id, newData);
			}
		},
		[onUpdateCardData, card.id]
	);

	const handleSaveSettings = useCallback(
		(newData) => {
			handleUpdateCardData(newData);
			setShowSettings(false);
		},
		[handleUpdateCardData]
	);

	const renderCardContent = () => {
		const cardType = card.type || CARD_TYPES.CALL_TO_ACTION;

		switch (cardType) {
			case CARD_TYPES.CALL_TO_ACTION:
			case CARD_TYPES.CALL_TO_ACTION_INTERNAL:
			case CARD_TYPES.CALL_TO_ACTION_GENERAL:
			case CARD_TYPES.CALL_TO_ACTION_LEAD:
			case CARD_TYPES.CALL_TO_ACTION_REFERRAL:
			case CARD_TYPES.CALL_TO_ACTION_RECOGNITION:
				return (
					<CallToActionCard
						card={card}
						onUpdate={handleUpdateCardData}
						isLocked={isLocked}
						isEditMode={isEditMode}
						isDragging={isDragging}
						isResizing={isResizing}
						isHovered={isHovered}
					/>
				);
			case CARD_TYPES.JOB_ALERTS:
				return (
					<JobAlertsCard
						card={card}
						onUpdate={handleUpdateCardData}
						isLocked={isLocked}
						isEditMode={isEditMode}
						isDragging={isDragging}
						isResizing={isResizing}
						isHovered={isHovered}
						currentUser={currentUser}
						setCurrentUser={setCurrentUser}
						departments={departments}
						subCompanies={subCompanies}
						jobNotificationSetting={jobNotificationSetting}
						hideSubCompany={hideSubCompany}
					/>
				);
			case CARD_TYPES.OPEN_JOBS:
				return (
					<OpenJobsCard
						card={card}
						isEditMode={isEditMode}
						isDragging={isDragging}
						isResizing={isResizing}
						isHovered={isHovered}
						userStats={userStats}
						isAdminView={isAdminView}
					/>
				);
			case CARD_TYPES.LEADERBOARD:
				return (
					<LeaderboardCard
						card={card}
						isEditMode={isEditMode}
						isDragging={isDragging}
						isResizing={isResizing}
						isHovered={isHovered}
						referralTotalLeaderboard={leaderboards?.referralTotal}
						referralPointsLeaderboard={leaderboards?.referralPoints}
					/>
				);
			case CARD_TYPES.YOUR_REFERRALS:
				return (
					<YourReferralsCard
						card={card}
						isEditMode={isEditMode}
						isDragging={isDragging}
						isResizing={isResizing}
						isHovered={isHovered}
						userStats={userStats}
						isAdminView={isAdminView}
					/>
				);
			case CARD_TYPES.POINTS_RANKING:
				return (
					<PointsRankingCard
						card={card}
						isEditMode={isEditMode}
						isDragging={isDragging}
						isResizing={isResizing}
						isHovered={isHovered}
						userStats={userStats}
						isAdminView={isAdminView}
					/>
				);
			case CARD_TYPES.REFERRALS_RANKING:
				return (
					<ReferralsRankingCard
						card={card}
						isEditMode={isEditMode}
						isDragging={isDragging}
						isResizing={isResizing}
						isHovered={isHovered}
						userStats={userStats}
						isAdminView={isAdminView}
					/>
				);
			case CARD_TYPES.CUSTOM_HTML:
				return (
					<CustomHtmlCard
						card={card}
						isEditMode={isEditMode}
						isDragging={isDragging}
						isResizing={isResizing}
						isHovered={isHovered}
					/>
				);
			case CARD_TYPES.ANNOUNCEMENTS:
				return (
					<AnnouncementsCard
						card={card}
						isEditMode={isEditMode}
						isDragging={isDragging}
						isResizing={isResizing}
						isHovered={isHovered}
						announcements={announcements}
						currentUser={currentUser}
					/>
				);
			case CARD_TYPES.RECENT_JOBS:
				return (
					<RecentJobsCard
						card={card}
						isEditMode={isEditMode}
						isDragging={isDragging}
						isResizing={isResizing}
						isHovered={isHovered}
						hotSearchedJobs={hotSearchedJobs}
						currentUser={currentUser}
					/>
				);
			case CARD_TYPES.ANNOUNCEMENT_THUMBNAIL:
				return (
					<AnnouncementThumbnailCard
						card={card}
						isEditMode={isEditMode}
						isDragging={isDragging}
						isResizing={isResizing}
						isHovered={isHovered}
						announcements={announcements}
					/>
				);
			case CARD_TYPES.RECOGNITION_SHOUTOUTS:
				return (
					<RecognitionShoutoutsCard
						card={card}
						isEditMode={isEditMode}
						isDragging={isDragging}
						isResizing={isResizing}
						isHovered={isHovered}
						recognitionShoutouts={recognitionShoutouts}
						companyName={currentUser?.company?.name}
					/>
				);
			case CARD_TYPES.MY_RECOGNITIONS:
				return (
					<MyRecognitionsCard
						card={card}
						isEditMode={isEditMode}
						isDragging={isDragging}
						isResizing={isResizing}
						isHovered={isHovered}
						myRewards={myRewards}
					/>
				);
			case CARD_TYPES.RECOGNITION_LEADERBOARD:
				return (
					<RecognitionLeaderboardCard
						card={card}
						isEditMode={isEditMode}
						isDragging={isDragging}
						isResizing={isResizing}
						isHovered={isHovered}
						currentUser={currentUser}
						recognitionPointsLeaderboard={leaderboards?.recognitionPoints}
						recognitionPointsBySubCompanyLeaderboard={
							leaderboards?.recognitionPointsBySubCompany
						}
						isLoading={userStatsLoading}
					/>
				);
			default:
				return (
					<div className="w-full h-full flex items-center justify-center text-gray-400">
						<span className="text-sm">Card Content: {card.id}</span>
					</div>
				);
		}
	};

	return (
		<div
			ref={cardRef}
			className={`absolute select-none ${
				card.type
					? ''
					: `bg-white rounded-lg shadow-md border-2 ${
							isDragging
								? 'shadow-2xl scale-105 border-blue-400 z-50 transition-none'
								: isResizing
									? 'shadow-2xl border-blue-400 z-50 transition-none'
									: isLocked
										? 'border-gray-200 opacity-80'
										: isEditMode
											? 'border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all duration-200'
											: 'border-gray-200'
						}`
			} ${isEditMode ? getCursorClass() : 'cursor-default'} ${
				isDragging ? 'z-50' : isResizing ? 'z-50' : ''
			}`}
			style={{
				width: `${displayWidth}px`,
				height: `${displayHeight}px`,
				transform: `translate(${displayX}px, ${displayY}px)`,
				willChange:
					isDragging || isResizing ? 'transform, width, height' : undefined,
			}}
			onMouseDown={handleMouseDown}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			{renderCardContent()}

			{isEditMode && isHovered && !isDragging && !isResizing && (
				<>
					<button
						className="delete-button absolute top-2 right-2 p-1.5 bg-red-500 text-white hover:bg-red-600 transition-colors shadow-md z-10"
						style={{ borderRadius: '50%' }}
						onClick={(e) => {
							e.stopPropagation();
							onDelete();
						}}
					>
						<LuX size={16} />
					</button>
					{card.type && (
						<button
							className="settings-button absolute top-2 right-12 p-1.5 bg-blue-500 text-white hover:bg-blue-600 transition-colors shadow-md z-10"
							style={{ borderRadius: '50%' }}
							onClick={(e) => {
								e.stopPropagation();
								setShowSettings(true);
							}}
						>
							<LuSettings size={16} />
						</button>
					)}
				</>
			)}

			{isEditMode && isHovered && !isDragging && !isResizing && !isLocked && (
				<>
					<div
						className="resize-handle absolute -bottom-2 -right-2 w-14 h-14 cursor-nwse-resize"
						onMouseDown={(e) => handleResizeMouseDown(e, 'both')}
						onMouseEnter={() => setHoveredHandle('both')}
						onMouseLeave={() => setHoveredHandle(null)}
					>
						<div
							className={`absolute bottom-2 right-2 w-12 h-12 border-r-2 border-b-2 transition-opacity duration-200 ${hoveredHandle === 'both' ? 'opacity-100' : 'opacity-0'}`}
							style={{
								borderBottomRightRadius: '14px',
								borderColor: '#74a9ffff',
							}}
						/>
					</div>
					<div
						className="resize-handle absolute -bottom-2 left-1/2 -translate-x-1/2 w-16 h-10 cursor-ns-resize"
						onMouseDown={(e) => handleResizeMouseDown(e, 'height')}
						onMouseEnter={() => setHoveredHandle('height')}
						onMouseLeave={() => setHoveredHandle(null)}
					>
						<div
							className={`absolute bottom-2 left-1/2 -translate-x-1/2 w-12 h-4 border-b-2 transition-opacity duration-200 ${hoveredHandle === 'height' ? 'opacity-100' : 'opacity-0'}`}
							style={{ borderColor: '#74a9ffff' }}
						/>
					</div>
					<div
						className="resize-handle absolute -right-2 top-1/2 -translate-y-1/2 w-10 h-16 cursor-ew-resize"
						onMouseDown={(e) => handleResizeMouseDown(e, 'width')}
						onMouseEnter={() => setHoveredHandle('width')}
						onMouseLeave={() => setHoveredHandle(null)}
					>
						<div
							className={`absolute right-2 top-1/2 -translate-y-1/2 w-4 h-12 border-r-2 transition-opacity duration-200 ${hoveredHandle === 'width' ? 'opacity-100' : 'opacity-0'}`}
							style={{ borderColor: '#74a9ffff' }}
						/>
					</div>
				</>
			)}

			<CardSettingsModal
				visible={showSettings}
				cardData={card.data}
				cardType={card.type}
				onSave={handleSaveSettings}
				onCancel={() => setShowSettings(false)}
				currentUser={currentUser}
			/>
		</div>
	);
});

export default Card;
